/**
 * CLI-001 — Helpers internes du module Clients (endpoints, headers,
 * query builders, invalidation des caches après mutation).
 */

import type { useQueryClient } from '@tanstack/react-query'

import { API_BASE } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'

import type {
    Client,
    ClientListFilters,
} from './useClients.types'

export const CLIENTS_ENDPOINT = `${API_BASE}/crm/clients`
export const CLIENTS_LISTING_ENDPOINT = '/api/crm/clients'
/** Le listing des AO (clé du `DataTable` serveur) : il affiche des champs du client. */
const TENDERS_LISTING_ENDPOINT = '/api/sales/tenders'

export function clientHeaders(entityId: string | null | undefined): HeadersInit {
    const h: Record<string, string> = { 'content-type': 'application/json' }
    if (entityId) h['x-entity-id'] = entityId
    return h
}

/**
 * Invalide tous les caches dépendant du listing clients après une mutation.
 *
 * Trois queryKeys cohabitent : factory module + DataTable serveur +
 * compteurs de groupes (`useGrouping`). Sans la 3e invalidation, le mode
 * regroupé reste figé après mutation. Cf. doctrine datatable-audit-expert §E.
 */
export function invalidateClientListings(
    qc: ReturnType<typeof useQueryClient>,
): void {
    void qc.invalidateQueries({ queryKey: queryKeys.clients.all, refetchType: 'all' })
    void qc.invalidateQueries({ queryKey: [CLIENTS_LISTING_ENDPOINT], refetchType: 'all' })
    void qc.invalidateQueries({ queryKey: ['datatable-groups', CLIENTS_LISTING_ENDPOINT], refetchType: 'all' })
    // Le listing des AO porte le NOM du client et son contact administratif
    // (`tenders-person-columns.tsx` ③, lu dans `clients.admin_owner_id`). Sans
    // ceci, il gardait l'ancienne personne jusqu'à 30 s (`staleTime` global).
    // Refetch par défaut (`active`) : une liste non montée est seulement
    // marquée périmée, et se recharge quand on y revient.
    void qc.invalidateQueries({ queryKey: [TENDERS_LISTING_ENDPOINT] })
    void qc.invalidateQueries({ queryKey: ['datatable-groups', TENDERS_LISTING_ENDPOINT] })
    // Le KANBAN des AO et la FICHE d'un AO lisent les mêmes champs du client (son nom, le
    // contact administratif de l'équipe) : sans eux, ils restaient périmés jusqu'à 30 s.
    void qc.invalidateQueries({ queryKey: queryKeys.tenderBoards.all })
    void qc.invalidateQueries({ queryKey: queryKeys.tenders.detailsAll() })
}

export function buildClientQuery(filters?: ClientListFilters): string {
    if (!filters) return ''
    const sp = new URLSearchParams()
    // Le backend (clients_read_routes) déclare `Query(alias="search")` — on
    // doit donc envoyer `?search=…` et pas `?q=…`. Ce paramètre côté Python
    // s'appelle `q` mais l'URL exposée est `search` (alias FastAPI). Bug
    // historique resté caché car la DataTable du listing utilise sa propre
    // construction d'URL (envoie déjà `search`) — seul ClientPicker lisait
    // sans filtrer (corrigé 2026-05-06).
    if (filters.q) sp.set('search', filters.q)
    filters.status?.forEach((s) => sp.append('status', s))
    filters.client_type?.forEach((t) => sp.append('client_type', t))
    filters.roles?.forEach((r) => sp.append('roles', r))
    if (filters.sales_owner_id) sp.set('sales_owner_id', filters.sales_owner_id)
    if (filters.admin_owner_id) sp.set('admin_owner_id', filters.admin_owner_id)
    if (filters.lead_type_id) sp.set('lead_type_id', filters.lead_type_id)
    if (filters.lead_source_id) sp.set('lead_source_id', filters.lead_source_id)
    if (filters.is_active !== undefined) sp.set('is_active', String(filters.is_active))
    if (filters.only_without_recent_session !== undefined)
        sp.set('only_without_recent_session', String(filters.only_without_recent_session))
    if (filters.include_descendants !== undefined)
        sp.set('include_descendants', String(filters.include_descendants))
    if (filters.cursor) sp.set('cursor', filters.cursor)
    if (filters.limit) sp.set('limit', String(filters.limit))
    if (filters.sort) sp.set('sort', filters.sort)
    const qs = sp.toString()
    return qs ? `?${qs}` : ''
}

/**
 * Détecte qu'un enrichissement asynchrone est en cours côté backend.
 *
 * Vrai si `enrichment_requested_at` est postérieur à `enrichment_fetched_at`
 * (ou si fetched_at est null). Sert à activer le poll auto de la fiche
 * tant que les sources lentes (INPI, BODACC) n'ont pas répondu.
 *
 * Garde-fou : si requested_at date de plus de 90 s, on arrête de poller
 * (l'enrichissement a probablement crashé silencieusement — un retry manuel
 * via le bouton « Synchroniser » reste possible).
 */
export function isEnrichmentInProgress(client: Client): boolean {
    const requested = client.enrichment.enrichment_requested_at
    const fetched = client.enrichment.enrichment_fetched_at
    if (!requested) return false
    const requestedDate = new Date(requested)
    const elapsedMs = Date.now() - requestedDate.getTime()
    if (elapsedMs > 90_000) return false
    if (!fetched) return true
    return new Date(fetched) < requestedDate
}

/**
 * P-M3 (audit 2026-05-08) — Backoff exponentiel sur le polling enrichment.
 * Avant : intervalle fixe 2 s pendant 90 s → jusqu'à 45 requêtes par création
 * de client. Avec backoff (2s → 4s → 8s plafonné à 10s) on tombe à ~12-15
 * requêtes pour la même fenêtre, gain ~70 %. Le 1er hit reste rapide pour
 * les enrichissements courts (cas dominant). Le timestamp `requested_at`
 * sert de point de référence pour calculer la phase courante.
 */
export function enrichmentPollDelay(client: Client): number {
    const requested = client.enrichment.enrichment_requested_at
    if (!requested) return 2_000
    const elapsedMs = Date.now() - new Date(requested).getTime()
    if (elapsedMs < 4_000) return 2_000   // 0→4s : 2 hits rapides
    if (elapsedMs < 12_000) return 4_000  // 4→12s : 2 hits
    if (elapsedMs < 30_000) return 8_000  // 12→30s : ~2 hits
    return 10_000                          // 30→90s : 1 hit toutes les 10s
}
